import { useFormik } from "formik";
import { useEffect, useState } from "react";
import {
  Button,
  CardList,
  Dropzone,
  StepNextButton,
} from "../../../components";
import {
  CardVariantType,
  DisplayVariant,
} from "../../../components/CardList/CardList";
import { useLoanApplication } from "../../../contexts";
import { useDeleteMedia, useMedia } from "../../../hooks";
import {
  getDropzoneAcceptForPurpose,
  getInputAcceptForPurpose,
  getExtensionsHintForPurpose,
} from "../../../utils/accept.util";
import { MediaPurpose, MediaType } from "../../../hooks/api/types";
import {
  CustomFile,
  DOCUMENTS_UPLOAD_FORM_INITIAL_VALUES,
  VatCertificateUploadFormFields,
} from "./service";
import { PdfIcon } from "../../../assets/icons";
import { formatBytes } from "../../../utils";
import { getMediaFromStatementIds } from "../../LoanCalculator/service";
import { getFileIcon } from "../../../utils/fileIcon.util";

interface VATCertificateUploadProps {
  loanApplicationId?: string;
  closeModal: () => void;
}

const VATCertificateUpload: React.FC<VATCertificateUploadProps> = ({
  closeModal,
}) => {
  const [certificates, setCertificates] = useState<CustomFile[]>([]);
  const [certificateIds, setCertificateIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState<boolean[]>([]);
  const [isFailedUploadExist, setIsFailedUploadExist] = useState(false);

  const {
    state,
    actions: { update },
  } = useLoanApplication();

  const { upload, get } = useMedia();
  const { mutateAsync: deleteMediaById } = useDeleteMedia();
  useEffect(() => {
    if (
      state.docVatCertificates.length &&
      !state.docVatCertificateFiles.length
    ) {
      getMediaFromStatementIds(certificateIds, get, (retrievedMedia) => {
        setCertificates([...retrievedMedia]);
        update({ docVatCertificateFiles: [...retrievedMedia] });
      });
    } else if (state.docVatCertificateFiles.length) {
      setCertificates([...state.docVatCertificateFiles]);
    }
  }, [certificateIds]);

  useEffect(() => {
    let anyFailed = false;
    certificates.forEach((c) => {
      if (c.error) anyFailed = true;
    });
    if (!anyFailed) setIsFailedUploadExist(false);
  }, [certificates]);

  const { values, handleSubmit, setErrors, validateForm } =
    useFormik<VatCertificateUploadFormFields>({
      initialValues: {
        ...DOCUMENTS_UPLOAD_FORM_INITIAL_VALUES,
        vatCertificates: [],
      },
      onSubmit: async () => {
        if (!values.vatCertificates.length) return;
        setUploading((prev) => values.vatCertificates.map(() => true));
        setLoading(true);
        const uploadPromises = values.vatCertificates.map((file) =>
          upload({
            file,
            type: MediaType.DOCUMENT,
            purpose: MediaPurpose.VAT_CERTIFICATE,
            loanApplicationId: state.id,
          }).catch(() => {
            file.error = "Upload failed";
            setIsFailedUploadExist(true);
            setLoading(false);
          })
        );
        const newIds = (await Promise.all(uploadPromises)).filter(
          Boolean
        ) as string[];
        setCertificates((prev) => {
          const next = prev.map((c, idx) => {
            if (!c.id) c.id = newIds[idx];
            return c;
          });
          return next;
        });
        setUploading(values.vatCertificates.map(() => false));
        update({
          docVatCertificateFiles: getFilesToUpdate(),
          docVatCertificates: [...state.docVatCertificates, ...newIds],
        }).then(() => {
          setLoading(false);
          if (!isFailedUploadExist) closeModal();
        });
      },
    });

  useEffect(() => {
    setCertificateIds(state.docVatCertificates);
  }, [state.docVatCertificates]);

  const getFilesToUpdate = () =>
    state.docVatCertificates.length
      ? [...state.docVatCertificateFiles, ...values.vatCertificates]
      : [...certificates];

  const handleFileUpload = (files: File[]) => {
    setErrors({});
    if (!files) return;
    const updated = [...values.vatCertificates];
    files.forEach((file) => {
      const all = values.vatCertificates.concat(certificates);
      if (!all.some((f) => f.name === file.name)) {
        setUploading((prev) => [...prev, false]);
        updated.push(new CustomFile(file));
      }
    });
    values.vatCertificates = [...updated];
    state.docVatCertificateFiles.length
      ? setCertificates((prev) => [...updated, ...prev])
      : setCertificates([...updated]);
  };

  const handleFileClose = (fileName: string, id?: string) => {
    if (id) {
      deleteMediaById(id).then(() => {
        setCertificates((prev) => prev.filter((c) => c.id !== id));
        update({
          docVatCertificateFiles: state.docVatCertificateFiles.filter(
            (f) => f.id !== id
          ),
          docVatCertificates: state.docVatCertificates.filter(
            (cid) => cid !== id
          ),
        });
      });
    } else if (fileName) {
      values.vatCertificates = values.vatCertificates.filter(
        (c) => c.name !== fileName
      );
      setCertificates((prev) => prev.filter((c) => c.name !== fileName));
      validateForm();
    }
  };

  const handleRetryUpload = (fileName: string) => {
    const file = values.vatCertificates.find((c) => c.name === fileName);
    if (!file) return;
    let idx = 0;
    certificates.forEach((c, i) => {
      if (c.name === fileName) idx = i;
    });
    setUploading((prev) => prev.map((_, i) => i === idx));
    upload({
      file,
      type: MediaType.DOCUMENT,
      purpose: MediaPurpose.VAT_CERTIFICATE,
      loanApplicationId: state.id,
    })
      .then((id: string) => {
        setUploading((prev) => prev.map(() => false));
        values.vatCertificates = values.vatCertificates.filter(
          (c) => c.name !== fileName
        );
        setCertificates((prev) =>
          prev.map((c, i) => (i === idx ? { ...c, error: "", id } : c))
        );
        update({
          docVatCertificateFiles: [...state.docVatCertificateFiles, file],
        });
      })
      .catch(() => setUploading((prev) => prev.map(() => false)));
  };

  return (
    <div className="cx-flex cx-flex-col cx-items-center">
      <div className="cx-text-text-secondary cx-text-center cx-mb-10">
        Upload your VAT certificate(s).
      </div>
      <div className="cx-w-full cx-max-w-[400px]">
        <div className="cx-mb-10">
          <Dropzone
            variant="simple"
            extensions={getExtensionsHintForPurpose(
              MediaPurpose.VAT_CERTIFICATE,
              5
            )}
            accept={getDropzoneAcceptForPurpose(MediaPurpose.VAT_CERTIFICATE)}
            onDrop={handleFileUpload}
          />
        </div>
        <form onSubmit={handleSubmit}>
          {certificates.map((c, index) => (
            <div className="cx-mb-2" key={index}>
              <CardList
                variant={
                  c.error
                    ? CardVariantType.UPLOAD_FAILED
                    : CardVariantType.UPLOADED
                }
                title={c.name}
                description={c.error ? c.error : formatBytes(c.size, 2)}
                fullWidth
                icon={getFileIcon(c)}
                inputProps={{
                  accept: getInputAcceptForPurpose(
                    MediaPurpose.VAT_CERTIFICATE
                  ),
                }}
                error={!!c.error}
                handleFileClose={handleFileClose}
                handleRetryUpload={handleRetryUpload}
                uploading={uploading[index]}
                documentId={c.id}
                truncateTitleLength={35}
                displayVariant={DisplayVariant.NEW}
              />
            </div>
          ))}
          <div className="cx-w-full cx-flex cx-flex-col cx-gap-x-4 cx-mt-10">
            <StepNextButton
              label={"Submit"}
              type="submit"
              disabled={
                isFailedUploadExist ||
                (!values.vatCertificates.length && certificates.length === 0)
              }
              loading={loading}
            />
            <Button label={"Cancel"} fullWidth secondary onClick={closeModal} />
          </div>
        </form>
      </div>
    </div>
  );
};

export default VATCertificateUpload;
